/* sts-float-row.js: the one floating save row (prototype v6.1, handoff 1.6
   "Save buttons float in their area, everywhere"). Every editable area uses
   this; do not write another float check.

     STS.floatRow(area, row)  bind `row` (the area's button row, inside it) to
                              `area` (the card being edited). Returns
                              { check(), off() }; binding a row twice returns
                              the first handle.
     STS.floatRow.check()     re-decide every row now, after a change that
                              moved the layout without a scroll or a resize

   Markup does it with no script of your own (bound once this file runs):
     <section data-sts-float-area> ... <div data-sts-float-row>buttons</div> </section>

   The rule, from the prototype's checkSticky(): while the area is on screen
   and its row's spot is below the float line, the row floats at the bottom
   of the viewport at the area's width, 12px in from each side (white, 1px
   line, 18px radius, soft shadow). The line is 12px above the bottom edge on
   desktop, 12px above the phone bottom menu (--sts-bottom-offset,
   assets/sts-bottom-nav.css) and above the Skin Profile selection bar
   (--sts-bar-h, 0 elsewhere). The row settles back when its spot comes up to
   the line, waits until 160px of the area (or the row's height, if taller)
   shows above the line so it never hangs outside its area, and never floats
   when the whole area fits on screen. A hidden placeholder with the row's
   height and margins keeps its spot, so nothing below moves.

   Published:
     html[data-sts-float-active]  while any row floats; the Back to top arrow
                                  (assets/sts-jump.js) hides
     --sts-float-lift on :root    floating row height + 12px, else 0px; the
                                  staff simulator rises by it
     `sts:floatrow` on the row    detail.floating, each time it changes
   The rules ship with the script, as in assets/sts-jump.js: only the pages
   that load it pay for them. */
(function (w, d) {
  'use strict';

  var STS = w.STS = w.STS || {};
  if (STS.floatRow) return;

  var root = d.documentElement;
  var GAP = 12;
  var SHOWN = 160;
  var LINE = 'calc(var(--sts-bottom-offset,var(--sts-safe-bottom,0px)) + var(--sts-bar-h,0px) + 12px)';
  var ON = '[data-sts-float-row][data-sts-floating]';
  var CSS = '.sts-float-probe{position:fixed;left:0;bottom:' + LINE + ';width:0;height:0;visibility:hidden;pointer-events:none}' +
    '.sts-float-ph{display:block;flex:none;padding:0;border:0;visibility:hidden;pointer-events:none}' +
    ON + '{position:fixed;z-index:46;left:var(--sts-float-x);width:var(--sts-float-w);bottom:' + LINE + ';' +
    'box-sizing:border-box;margin:0;padding:10px 12px;background:var(--sts-surface,#fff);border:1px solid var(--sts-line,#DBDCDD);' +
    'border-radius:18px;box-shadow:var(--sts-shadow-float,0 12px 34px rgba(30,33,64,.18));animation:sts-float-in .2s ease}' +
    '@keyframes sts-float-in{from{opacity:0;transform:translateY(8px)}}' +
    'html[data-sts-float-active] .sts-totop{opacity:0;visibility:hidden}' +
    '@media (prefers-reduced-motion:reduce){' + ON + '{animation:none}}' +
    '@media print{' + ON + '{position:static;box-shadow:none}.sts-float-ph{display:none}}';

  var list = [];
  var probe = null;
  var ro = null;
  var frame = 0;
  var active = false;
  var lift = '';

  function queue() { if (!frame) frame = w.requestAnimationFrame(check); }

  function boot() {
    if (probe) return;
    var s = d.createElement('style');
    s.textContent = CSS;
    d.head.appendChild(s);
    /* The line, read off a 0px fixed element with the row's own `bottom`, so
       the CSS variables decide it once, in one place. */
    probe = d.createElement('i');
    probe.className = 'sts-float-probe';
    probe.setAttribute('aria-hidden', 'true');
    d.body.appendChild(probe);
    w.addEventListener('scroll', queue, { passive: true });
    w.addEventListener('resize', queue);
    d.addEventListener('focusin', reveal);
    /* The body catches content above an area growing (rows and apps that
       load late); each area is observed as well (opening, chips arriving). */
    if (w.ResizeObserver) { ro = new w.ResizeObserver(queue); ro.observe(d.body); }
  }

  function prop(e, k, v) {
    if (e[k] === v) return;
    e[k] = v;
    e.row.style.setProperty('--sts-float-' + k, v);
  }

  function place(e) {
    var a = e.area.getBoundingClientRect();
    prop(e, 'x', Math.round(a.left + GAP) + 'px');
    prop(e, 'w', Math.max(0, Math.round(a.width - 2 * GAP)) + 'px');
  }

  function set(e, on) {
    var row = e.row;
    if (on) {
      var cs = w.getComputedStyle(row);
      e.ph.style.height = row.offsetHeight + 'px';
      e.ph.style.margin = cs.marginTop + ' 0 ' + cs.marginBottom;
      row.parentNode.insertBefore(e.ph, row);
      place(e);
      row.setAttribute('data-sts-floating', '');
    } else {
      row.removeAttribute('data-sts-floating');
      if (e.ph.parentNode) e.ph.parentNode.removeChild(e.ph);
    }
    e.on = on;
    try { row.dispatchEvent(new CustomEvent('sts:floatrow', { bubbles: true, detail: { floating: on } })); } catch (x) { /* old engine */ }
  }

  function wants(e, line) {
    if (!e.row.getClientRects().length) return false; /* hidden, or inside something hidden */
    var spot = (e.on ? e.ph : e.row).getBoundingClientRect();
    var a = e.area.getBoundingClientRect();
    /* Floating adds the card's padding and border, about 22px. */
    var tall = e.on ? e.row.offsetHeight : spot.height + 24;
    return spot.height > 0 && a.height > line && spot.bottom > line + 0.5 &&
      a.top < line - Math.max(SHOWN, tall + GAP);
  }

  function check() {
    frame = 0;
    if (!probe) return;
    var line = probe.getBoundingClientRect().top;
    var most = 0;
    for (var i = list.length - 1; i >= 0; i--) {
      var e = list[i];
      if (!e.row.isConnected || !e.area.isConnected) { drop(e); continue; }
      var on = wants(e, line);
      if (on !== e.on) set(e, on);
      else if (on) place(e);
      if (on) most = Math.max(most, e.row.offsetHeight + GAP);
    }
    if (!!most !== active) root.toggleAttribute('data-sts-float-active', active = !!most);
    if (most + 'px' !== lift) root.style.setProperty('--sts-float-lift', lift = most + 'px');
  }

  /* Keyboard focus is never left under a floating row (WCAG 2.4.11): the
     browser thinks a control behind a fixed element is in view. */
  function reveal(ev) {
    var t = ev.target;
    list.forEach(function (e) {
      if (!e.on || !t.getBoundingClientRect || !e.area.contains(t) || e.row.contains(t)) return;
      var by = t.getBoundingClientRect().bottom - e.row.getBoundingClientRect().top + GAP;
      if (by <= 0) return;
      try { w.scrollBy({ top: by, behavior: 'instant' }); } catch (x) { w.scrollBy(0, by); }
    });
  }

  function drop(e) {
    var i = list.indexOf(e);
    if (i < 0) return;
    if (e.on) set(e, false);
    list.splice(i, 1);
    var shared = list.some(function (o) { return o.area === e.area; });
    if (ro && !shared) ro.unobserve(e.area);
    queue();
  }

  function floatRow(area, row) {
    if (!area || !row || area === row || !area.contains(row)) return null;
    for (var i = 0; i < list.length; i++) if (list[i].row === row) return list[i].api;
    boot();
    var ph = d.createElement('div');
    ph.className = 'sts-float-ph';
    ph.setAttribute('aria-hidden', 'true');
    var e = { area: area, row: row, ph: ph, on: false };
    e.api = { check: queue, off: function () { drop(e); } };
    row.setAttribute('data-sts-float-row', '');
    list.push(e);
    if (ro) ro.observe(area);
    queue();
    return e.api;
  }

  floatRow.check = queue;
  STS.floatRow = floatRow;

  function scan() {
    var rows = d.querySelectorAll('[data-sts-float-row]');
    for (var i = 0; i < rows.length; i++) {
      var up = rows[i].parentElement;
      var area = up && up.closest('[data-sts-float-area]');
      if (area) floatRow(area, rows[i]);
    }
  }
  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', scan);
  else scan();
}(window, document));
